import { useState, useEffect, useCallback } from "react";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Link, Navigate } from "react-router-dom";
import Logo from "../components/Logo";
import {
  Banknote,
  Download,
  ChevronLeft,
  Loader2,
  CheckCircle2,
  Clock,
  DollarSign,
  RefreshCw,
  AlertTriangle
} from "lucide-react";

const ADMIN_EMAILS = ["reynir@replydriven.com"];

const AdminPayouts = () => {
  const { user } = useAuth();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalReady, setTotalReady] = useState(0);
  const [payoutRequestsCount, setPayoutRequestsCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmModal, setConfirmModal] = useState(false);
  const [processing, setProcessing] = useState(false);

  const isAdmin = user && ADMIN_EMAILS.includes(user.email?.toLowerCase());

  const fetchPayouts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/payouts?status=ready");
      setPayouts(res.data?.payouts || []);
      setTotalReady(res.data?.total_ready || 0);
      setPayoutRequestsCount(res.data?.payout_requests_count || 0);
    } catch (err) {
      toast.error("Failed to load payouts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchPayouts();
    }
  }, [isAdmin, fetchPayouts]);

  const toggleSelect = (affiliateId) => {
    setSelectedIds(prev => 
      prev.includes(affiliateId) 
        ? prev.filter(id => id !== affiliateId)
        : [...prev, affiliateId]
    );
  };

  const selectAll = () => {
    if (selectedIds.length === payouts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(payouts.map(p => p.affiliate_id));
    }
  };

  const handleMarkPaid = async () => {
    if (selectedIds.length === 0) return;
    
    setProcessing(true);
    try {
      const res = await api.post("/admin/payouts/mark-paid", {
        affiliate_ids: selectedIds
      });
      toast.success(`Marked ${res.data.commissions_marked} commissions as paid ($${res.data.total_paid})`);
      setSelectedIds([]);
      setConfirmModal(false);
      fetchPayouts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to mark as paid");
    } finally {
      setProcessing(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const res = await api.get("/admin/payouts/export-csv");
      if (!res.data.csv) {
        toast.error("No payouts to export");
        return;
      }
      
      // Download CSV
      const blob = new Blob([res.data.csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `paypal_payouts_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${res.data.count} payouts ($${res.data.total})`);
    } catch (err) {
      toast.error("Failed to export CSV");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleDateString("en-US", { 
      month: "short", 
      day: "numeric", 
      year: "numeric" 
    });
  };

  const selectedTotal = payouts
    .filter(p => selectedIds.includes(p.affiliate_id))
    .reduce((sum, p) => sum + p.amount_ready, 0);

  if (!user) return <Navigate to="/" />;
  if (!isAdmin) return <Navigate to="/dashboard" />;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <Logo showText linkTo="/" />
            <span className="text-text-muted hidden sm:inline">/</span>
            <Link to="/dashboard" className="text-text-muted hover:text-white text-sm hidden sm:inline">Dashboard</Link>
            <span className="text-text-muted hidden sm:inline">/</span>
            <span className="text-white text-sm font-medium">Admin</span>
          </div>
          <Link to="/dashboard" className="flex-shrink-0">
            <Button variant="ghost" size="sm" className="text-text-muted hover:text-white px-2 sm:px-4">
              <ChevronLeft className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Back</span>
            </Button>
          </Link>
        </div>
      </nav>

      {/* Admin Sub-Navigation */}
      <div className="fixed top-[57px] sm:top-[65px] left-0 right-0 z-40 bg-[#0a0a0a] border-b border-[#262626] overflow-x-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-1 min-w-max">
            <Link to="/admin" className="px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 border-transparent text-text-muted hover:text-white whitespace-nowrap">Overview</Link>
            <Link to="/admin/affiliates" className="px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 border-transparent text-text-muted hover:text-white whitespace-nowrap">Affiliates</Link>
            <Link to="/admin/users" className="px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 border-transparent text-text-muted hover:text-white whitespace-nowrap">Users</Link>
            <Link to="/admin/customers" className="px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 border-transparent text-text-muted hover:text-white whitespace-nowrap">Customers</Link>
            <Link to="/admin/payouts" className="px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 border-[#D4F34A] text-white whitespace-nowrap">Payouts</Link>
          </div>
        </div>
      </div>

      <main className="pt-28 sm:pt-32 pb-12 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                <Banknote className="w-8 h-8 text-[#D4F34A]" />
                Payouts
              </h1>
              <p className="text-text-muted text-sm mt-1">
                Process affiliate payouts via PayPal
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={handleExportCSV}
                className="border-[#262626] text-white hover:bg-white/5"
              >
                <Download className="w-4 h-4 mr-2" /> Export PayPal CSV
              </Button>
              <Button
                variant="outline"
                onClick={fetchPayouts}
                className="border-[#262626] text-text-muted hover:text-white"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <Card className="bg-surface border-[#262626]">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#D4F34A]/10 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-[#D4F34A]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#D4F34A]">${totalReady.toFixed(2)}</p>
                    <p className="text-xs text-text-muted">Total Ready to Pay</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-surface border-[#262626]">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{payoutRequestsCount}</p>
                    <p className="text-xs text-text-muted">Payout Requests</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-surface border-[#262626]">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{payouts.length}</p>
                    <p className="text-xs text-text-muted">Affiliates to Pay</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Action Bar */}
          {selectedIds.length > 0 && (
            <Card className="bg-[#D4F34A]/10 border-[#D4F34A]/30 mb-4">
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm">
                    <span className="font-bold text-[#D4F34A]">{selectedIds.length}</span> affiliates selected
                    <span className="mx-2">•</span>
                    <span className="font-bold text-white">${selectedTotal.toFixed(2)}</span> total
                  </p>
                  <Button
                    onClick={() => setConfirmModal(true)}
                    className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Mark Selected Paid
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Payouts Table */}
          <Card className="bg-surface border-[#262626]">
            <CardContent className="p-0">
              {loading ? (
                <div className="py-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-text-muted" />
                </div>
              ) : payouts.length === 0 ? (
                <div className="py-12 text-center text-text-muted">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No payouts ready</p>
                  <p className="text-sm mt-1">All affiliate commissions are either pending approval or already paid.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#262626] bg-[#0a0a0a]">
                        <th className="text-left py-3 px-4">
                          <Checkbox
                            checked={selectedIds.length === payouts.length}
                            onCheckedChange={selectAll}
                          />
                        </th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-text-muted uppercase">Affiliate</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-text-muted uppercase">PayPal Email</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-text-muted uppercase">Amount Ready</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-text-muted uppercase">Last Payout</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-text-muted uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#262626]">
                      {payouts.map((payout) => (
                        <tr key={payout.affiliate_id} className="hover:bg-white/5">
                          <td className="py-3 px-4">
                            <Checkbox
                              checked={selectedIds.includes(payout.affiliate_id)}
                              onCheckedChange={() => toggleSelect(payout.affiliate_id)}
                            />
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-white font-medium">{payout.name}</p>
                            <p className="text-xs text-text-muted">{payout.email}</p>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-white font-mono text-sm">{payout.payout_email}</p>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-[#D4F34A] font-bold text-lg">${payout.amount_ready.toFixed(2)}</p>
                          </td>
                          <td className="py-3 px-4 text-sm text-text-muted">
                            {formatDate(payout.last_payout_date)}
                          </td>
                          <td className="py-3 px-4">
                            {payout.payout_requested ? (
                              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                                <Clock className="w-3 h-3 mr-1" /> Requested
                              </Badge>
                            ) : (
                              <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">
                                Ready
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Confirm Modal */}
      <Dialog open={confirmModal} onOpenChange={setConfirmModal}>
        <DialogContent className="bg-[#1a1a1a] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Confirm Payout
            </DialogTitle>
            <DialogDescription className="text-text-muted">
              Mark {selectedIds.length} commissions as PAID. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-center text-3xl font-bold text-[#D4F34A]">
              ${selectedTotal.toFixed(2)}
            </p>
            <p className="text-center text-sm text-text-muted mt-1">
              to {selectedIds.length} affiliate{selectedIds.length !== 1 ? 's' : ''}
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmModal(false)} disabled={processing}>
              Cancel
            </Button>
            <Button
              onClick={handleMarkPaid}
              disabled={processing}
              className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Confirm & Mark Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPayouts;
